import React, { useState, useEffect } from 'react';
import QrScanner from 'react-qr-scanner';
import { ArrowLeft, QrCode, Keyboard } from 'lucide-react';

/**
 * PackageScanner Component
 * Allows users to scan packages via QR code or manual entry
 * Updates package status to 'in_fastmax' on the server
 * 
 * @param {Function} onBackClick - Callback when user clicks back button
 * @param {string|number} userId - Current user's ID for tracking who scanned the package
 */
function PackageScanner({ setActiveComponent, userId }) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'qr' | 'manual'
  const [showSuccess, setShowSuccess] = useState(false);
  const [manualId, setManualId] = useState('');
  const [driverId, setDriverId] = useState(null);
  const [district, setDistrict] = useState('');
  const [status, setStatus] = useState('');
  const [backCameraId, setBackCameraId] = useState(null);

  // Detect back camera on component mount
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const rear = devices.find(d =>
          d.kind === 'videoinput' &&
          /rear|back|environment/i.test(d.label)
        );
        setBackCameraId(rear?.deviceId || null);
      })
      .catch(err => console.error('Camera enumeration failed', err));
  }, []);

  // Auto-hide success banner after 3 seconds
  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(() => setShowSuccess(false), 3000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  /**
   * Update package status on server
   * @param {string} identifier - Package QR code or manual ID
   */
  const updateStatus = async (identifier) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/packages/${encodeURIComponent(identifier)}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_status: 'in_fastmax', user_id: userId })
        }
      );

      if (!res.ok) {
        console.error('Update failed', await res.text());
        alert('Error al actualizar el estado del paquete');
        return;
      }

      const data = await res.json();
      setDriverId(data.driver_id);
      setDistrict(data.district);
      setStatus(data.status);
      setShowSuccess(true);
    } catch (err) {
      console.error('Network error', err);
      alert('Error de conexión. Por favor intente nuevamente.');
    }
  };

  /**
   * Handle QR code scan result
   * @param {Object|string} result - QR scanner result
   */
  const handleQrScan = (result) => {
    if (!result) return;
    const code = typeof result === 'string' ? result : result?.text;
    if (!code) return;
    setMode('idle');
    updateStatus(code);
  };

  /**
   * Handle manual ID submission
   */
  const handleManualSubmit = () => {
    if (!manualId.trim()) {
      alert('Por favor ingrese un código válido');
      return;
    }
    setMode('idle');
    updateStatus(manualId);
    setManualId('');
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-4">
        <button
          onClick={() => setActiveComponent("OptionsAdmin")}
          className="flex items-center gap-2 text-gray-700 hover:text-brand-primary transition-colors"
        >
          <ArrowLeft size={24} />
          <span className="font-montserrat font-semibold">Volver</span>
        </button>
      </div>

      {/* Success banner */}
      {showSuccess && (
        <div className="absolute top-16 left-0 right-0 mx-4 bg-green-100 border-l-4 border-brand-green p-4 rounded shadow-lg z-50 animate-fade-in">
          <div className="flex items-start">
            <div className="flex-1">
              <p className="font-montserrat font-bold text-brand-green text-lg mb-2">
                ¡Estado actualizado exitosamente!
              </p>
              <div className="text-sm text-gray-700 space-y-1">
                <p><span className="font-semibold">Conductor ID:</span> {driverId || 'N/A'}</p>
                <p><span className="font-semibold">Distrito:</span> {district || 'N/A'}</p>
                <p><span className="font-semibold">Estado:</span> {status}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* QR Scanner Mode */}
        {mode === 'qr' && (
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-brand-primary p-4">
                <h2 className="font-montserrat font-bold text-white text-xl text-center">
                  Escanear Código QR
                </h2>
              </div>
              <div className="p-4">
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <QrScanner
                    delay={300}
                    onError={e => console.error('QR Scanner error:', e)}
                    onScan={handleQrScan}
                    style={{ width: '100%' }}
                    constraints={{
                      video: backCameraId
                        ? { deviceId: { exact: backCameraId } }
                        : { facingMode: { ideal: 'environment' } }
                    }}
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-4">
                  Apunta la cámara al código QR del paquete
                </p>
                <button
                  onClick={() => setMode('idle')}
                  className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Entry Mode - Custom Numeric Keypad */}
        {mode === 'manual' && (
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary/10 rounded-full mb-4">
                  <Keyboard className="text-brand-primary" size={32} />
                </div>
                <h2 className="font-montserrat font-bold text-gray-800 text-xl">
                  Ingresar Código Manualmente
                </h2>
              </div>

              <div className="space-y-4">
                {/* Display Area */}
                <div className="bg-gray-100 border-2 border-brand-primary rounded-lg p-6 min-h-[80px] flex items-center justify-center">
                  <div className="text-center w-full">
                    {manualId ? (
                      <p className="font-montserrat font-bold text-3xl text-gray-800 tracking-wider">
                        {manualId}
                      </p>
                    ) : (
                      <p className="font-montserrat text-gray-400 text-lg">
                        Ingrese el código
                      </p>
                    )}
                  </div>
                </div>

                {/* Custom Numeric Keypad */}
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button
                      key={num}
                      onClick={() => setManualId(prev => prev + num.toString())}
                      className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary"
                    >
                      {num}
                    </button>
                  ))}
                  
                  {/* Clear button */}
                  <button
                    onClick={() => setManualId('')}
                    className="bg-red-100 hover:bg-red-200 active:bg-red-300 text-red-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-red-200"
                  >
                    Borrar
                  </button>
                  
                  {/* Zero button */}
                  <button
                    onClick={() => setManualId(prev => prev + '0')}
                    className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary"
                  >
                    0
                  </button>
                  
                  {/* Backspace button */}
                  <button
                    onClick={() => setManualId(prev => prev.slice(0, -1))}
                    className="bg-orange-100 hover:bg-orange-200 active:bg-orange-300 text-orange-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-orange-200"
                  >
                    ←
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualId}
                    className={`flex-1 font-montserrat font-semibold py-4 px-6 rounded-lg transition-all ${
                      manualId
                        ? 'bg-brand-primary hover:bg-brand-primary/90 text-white'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => {
                      setMode('idle');
                      setManualId('');
                    }}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-4 px-6 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Idle Mode - Main Menu */}
        {mode === 'idle' && (
          <div className="w-full max-w-md space-y-4">
            <div className="text-center mb-8">
              <h1 className="font-montserrat font-extrabold text-3xl text-brand-primary mb-2">
                Escanear Paquete
              </h1>
              <p className="text-gray-600 font-montserrat">
                Seleccione el método de escaneo
              </p>
            </div>

            {/* QR Scan Button */}
            <button
              onClick={() => setMode('qr')}
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-6 px-6 rounded-xl shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3"
            >
              <QrCode size={32} />
              <span className="text-xl">Escanear Código QR</span>
            </button>

            {/* Manual Entry Button */}
            <button
              onClick={() => setMode('manual')}
              className="w-full bg-brand-green hover:bg-brand-green/90 text-white font-montserrat font-semibold py-6 px-6 rounded-xl shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3"
            >
              <Keyboard size={32} />
              <span className="text-xl">Ingresar Código</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PackageScanner;